/**
 * Native Lighthouse login + signup + confirm + forgot-password. No
 * AWS-hosted UI, no redirect off-site — all flows happen here inside
 * the React app, calling Cognito's API directly via amazon-cognito-
 * identity-js.
 *
 * The form is a tiny state machine between five "modes": signin,
 * signup, confirm, forgot, confirmForgot. Each mode renders the same
 * card with its own field set + submit handler.
 */
import { useState } from "react";
import { useAuth } from "./auth";

type Mode = "signin" | "signup" | "confirm" | "forgot" | "confirmForgot";

export function Login() {
  const { signIn, signUp, confirm, resendCode, forgot, confirmForgot } = useAuth();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const reset = () => { setErr(null); setInfo(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      if (mode === "signin") {
        await signIn(email, password);
      } else if (mode === "signup") {
        await signUp(email, password, name || undefined);
        setMode("confirm");
        setInfo(`We've sent a 6-digit code to ${email}. Enter it below to finish creating your account.`);
      } else if (mode === "confirm") {
        await confirm(email, code);
        await signIn(email, password);
      } else if (mode === "forgot") {
        await forgot(email);
        setMode("confirmForgot");
        setInfo(`We've sent a reset code to ${email}. Enter it below along with your new password.`);
      } else if (mode === "confirmForgot") {
        await confirmForgot(email, code, password);
        setMode("signin");
        setInfo("Password updated — sign in with the new one.");
      }
    } catch (e: any) {
      // Cognito errors come back as { code, name, message }. The message
      // is human-readable enough for direct display, with a few exceptions
      // we polish here.
      const msg: string = e?.message || String(e);
      if (msg.includes("UserNotFoundException")) {
        setErr("No account for that email. Tap 'Create one' below.");
      } else if (msg.includes("NotAuthorizedException")) {
        setErr("Incorrect email or password.");
      } else if (msg.includes("UsernameExistsException")) {
        setErr("An account already exists for that email. Sign in instead.");
      } else if (msg.includes("CodeMismatchException")) {
        setErr("That code didn't match. Check the email and try again.");
      } else if (msg.includes("ExpiredCodeException")) {
        setErr("The code has expired. Tap 'Resend' to get a fresh one.");
      } else if (msg.includes("InvalidPasswordException")) {
        setErr("Password must be at least 10 characters, include a digit and a lowercase letter.");
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    reset();
    setBusy(true);
    try {
      await resendCode(email);
      setInfo(`Fresh code sent to ${email}.`);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  // Field configs for each mode.
  const showEmail = mode !== "confirm" && mode !== "confirmForgot";
  const showPassword = mode === "signin" || mode === "signup" || mode === "confirmForgot";
  const showName = mode === "signup";
  const showCode = mode === "confirm" || mode === "confirmForgot";
  const ctaLabel =
    mode === "signin" ? "Sign in" :
    mode === "signup" ? "Create account" :
    mode === "confirm" ? "Verify & sign in" :
    mode === "forgot" ? "Send reset code" :
    "Set new password";
  const passwordLabel = mode === "confirmForgot" ? "New password" : "Password";

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="beam-stage">
          <div className="beam" />
          <div className="emblem">
            <svg width="68" height="68" viewBox="0 0 64 64" fill="none" aria-hidden>
              <path
                d="M32 6 L36 18 L36 22 L42 22 L40 28 L40 50 L46 56 L18 56 L24 50 L24 28 L22 22 L28 22 L28 18 Z"
                stroke="var(--beacon)" strokeWidth="1.6" strokeLinejoin="round"
                fill="rgba(255,180,61,0.06)"
              />
              <line x1="32" y1="6" x2="32" y2="2" stroke="var(--beacon)" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="32" cy="20" r="6" fill="var(--beacon)" opacity="0.85" />
              <circle cx="32" cy="20" r="14" fill="var(--beacon)" opacity="0.22" />
            </svg>
          </div>
        </div>

        <div className="login-name">
          <span className="lw-light">Light</span>
          <span className="lw-house">house</span>
        </div>
        <div className="login-by">BY THINKING FISH</div>

        <h2 style={{ margin: "26px 0 4px", fontFamily: "var(--font-display)" }}>
          {mode === "signin" && "Welcome back"}
          {mode === "signup" && "Create your account"}
          {mode === "confirm" && "Verify your email"}
          {mode === "forgot" && "Reset your password"}
          {mode === "confirmForgot" && "Set a new password"}
        </h2>
        <p style={{ color: "var(--text-1)", fontSize: 13, margin: "0 0 22px" }}>
          {mode === "signin" && "Sign in to see your monitored estates."}
          {mode === "signup" && "Your administrator will assign your access after signup."}
          {mode === "confirm" && "Paste the 6-digit code we sent to your inbox."}
          {mode === "forgot" && "Enter your email — we'll send you a reset code."}
          {mode === "confirmForgot" && "Use the code from your inbox + a new password."}
        </p>

        <form onSubmit={submit} className="login-form">
          {showName && (
            <Field label="Your name (optional)" type="text" value={name} onChange={setName} autoComplete="given-name" />
          )}
          {showEmail && (
            <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" autoFocus />
          )}
          {showCode && (
            <Field label="Verification code" type="text" inputMode="numeric" value={code} onChange={setCode} autoComplete="one-time-code" autoFocus />
          )}
          {showPassword && (
            <Field
              label={passwordLabel}
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              help={mode !== "signin" ? "≥ 10 chars · 1 digit · 1 lowercase" : undefined}
            />
          )}

          {err && <div className="login-msg login-msg-err">{err}</div>}
          {info && <div className="login-msg login-msg-info">{info}</div>}

          <button type="submit" disabled={busy} className="login-cta">
            {busy ? "…" : ctaLabel}
          </button>
        </form>

        <div className="login-foot">
          {mode === "signin" && (
            <>
              <button className="link" onClick={() => { reset(); setMode("signup"); }}>Create one</button>
              <span className="login-sep">·</span>
              <button className="link" onClick={() => { reset(); setMode("forgot"); }}>Forgot password</button>
            </>
          )}
          {mode === "signup" && (
            <>
              Already have an account?{" "}
              <button className="link" onClick={() => { reset(); setMode("signin"); }}>Sign in</button>
            </>
          )}
          {mode === "confirm" && (
            <>
              <button className="link" onClick={resend} disabled={busy}>Resend code</button>
              <span className="login-sep">·</span>
              <button className="link" onClick={() => { reset(); setMode("signin"); }}>Back to sign in</button>
            </>
          )}
          {(mode === "forgot" || mode === "confirmForgot") && (
            <button className="link" onClick={() => { reset(); setMode("signin"); }}>Back to sign in</button>
          )}
        </div>
      </div>

      <div className="login-status">
        <span className="dot ok live" />
        Monitoring <span style={{ color: "var(--text-0)" }}>Totteridge Manor</span>
        <span style={{ color: "var(--text-2)" }}> · private beta</span>
      </div>

      <style>{`
        .login-page { min-height: calc(100vh - 80px); display: grid; place-items: center; padding: 40px 20px 80px; position: relative; }
        .login-card {
          width: 100%; max-width: 460px;
          background: radial-gradient(700px 280px at 50% -40%, rgba(255,180,61,0.13), transparent 60%), var(--bg-1);
          border: 1px solid var(--border); border-radius: var(--radius-lg);
          padding: 36px 30px 28px; text-align: center;
          box-shadow: var(--shadow); position: relative; overflow: hidden;
        }
        .beam-stage { position: relative; height: 110px; margin: -10px 0 18px; }
        .beam {
          position: absolute; left: 50%; top: 8px; width: 280px; height: 280px; margin-left: -140px;
          background: conic-gradient(from 70deg at 50% 100%,
            transparent 0deg, transparent 75deg, rgba(255,180,61,0.18) 90deg, rgba(255,180,61,0.0) 110deg, transparent 360deg);
          filter: blur(1px); mix-blend-mode: screen; opacity: 0.9;
          animation: sweep 5.5s ease-in-out infinite; transform-origin: 50% 100%; pointer-events: none;
        }
        @keyframes sweep { 0% { transform: rotate(-22deg); } 50% { transform: rotate(22deg); } 100% { transform: rotate(-22deg); } }
        .emblem { position: absolute; inset: 0; display: grid; place-items: end center; padding-bottom: 6px; }
        .login-name { font-family: var(--font-display); font-weight: 600; font-size: 36px; letter-spacing: -0.02em; line-height: 1; }
        .login-name .lw-light { color: var(--text-0); } .login-name .lw-house { color: var(--beacon); }
        .login-by { font-size: 10.5px; color: var(--text-2); letter-spacing: 0.2em; margin-top: 8px; }

        .login-form { display: grid; gap: 14px; text-align: left; }
        .field-label { font-size: 11px; color: var(--text-2); text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 6px; display: block; }
        .field-help { font-size: 11px; color: var(--text-2); margin-top: 5px; }
        .login-input {
          width: 100%; box-sizing: border-box;
          background: var(--bg-0); color: var(--text-0);
          border: 1px solid var(--border); border-radius: 10px;
          padding: 11px 14px; font-size: 14px; font-family: var(--font-ui);
          transition: border-color 0.12s;
        }
        .login-input:focus { outline: none; border-color: var(--beacon); }

        .login-msg { padding: 10px 12px; border-radius: 8px; font-size: 12.5px; text-align: center; }
        .login-msg-err { background: var(--down-soft); color: var(--down); }
        .login-msg-info { background: var(--fish-soft); color: var(--fish); }

        .login-cta {
          background: var(--beacon); color: var(--bg-0);
          padding: 13px 26px; border-radius: 999px;
          font-weight: 700; letter-spacing: 0.06em; font-size: 13px; text-transform: uppercase;
          box-shadow: 0 12px 30px -16px rgba(255,180,61,0.55); transition: transform 0.12s;
          margin-top: 4px;
        }
        .login-cta:hover { transform: translateY(-1px); } .login-cta:active { transform: translateY(0); }
        .login-cta:disabled { opacity: 0.6; cursor: default; transform: none; }

        .login-foot { margin-top: 22px; font-size: 13px; color: var(--text-2); }
        .link { color: var(--text-0); text-decoration: underline; text-decoration-color: var(--border); text-underline-offset: 3px; padding: 0 2px; }
        .link:hover { text-decoration-color: var(--beacon); }
        .link:disabled { opacity: 0.5; }
        .login-sep { margin: 0 8px; color: var(--text-2); }

        .login-status { margin-top: 26px; font-size: 11px; color: var(--text-2); letter-spacing: 0.06em; display: inline-flex; align-items: center; gap: 10px; }

        @media (max-width: 520px) {
          .login-card { padding: 28px 20px 24px; border-radius: var(--radius); }
          .login-name { font-size: 30px; }
        }
      `}</style>
    </div>
  );
}

function Field({
  label, type, value, onChange, autoComplete, autoFocus, inputMode, help,
}: {
  label: string; type: string; value: string; onChange: (v: string) => void;
  autoComplete?: string; autoFocus?: boolean; inputMode?: any; help?: string;
}) {
  return (
    <label>
      <span className="field-label">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        inputMode={inputMode}
        required
        className="login-input"
      />
      {help && <div className="field-help">{help}</div>}
    </label>
  );
}
